import React, { useState, useEffect, useRef } from 'react';
import { format, parse, set, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO, getDay, startOfWeek, addDays, endOfWeek } from 'date-fns';
import './ReservationEdit.css';
import { useReservationById, useUpdateReservationFreeTable, useUpdateReservationWhole } from '../../api/reservations';
import { useRestaurant } from '../../api/restaurant';
import { useCustomerbyID } from '../../api/customers';
import { supabase } from '../../utils/supabase/client';
import { sendEmail } from '../../api/emails';
import { useUser } from '../../api/users';
import { useUpdateReservationInfoFreeTable } from '../../api/reservations';
import { supabaseFreeTable } from '../../utils/supabase/clientFreeTable';
import { useAuth } from '../../api/AuthProvider';

const CustomDatePicker = ({ selectedDate, onDateSelect, onClose }) => {
  const currentDate = selectedDate ? parseISO(selectedDate) : new Date();
  const [viewDate, setViewDate] = useState(currentDate);
  const { data, isLoading } = useRestaurant();

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const firstDayOfMonth = getDay(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = addDays(endOfWeek(monthEnd, { weekStartsOn: 1 }), 6);
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const monthNames = [
    'Јануари', 'Февруари', 'Март', 'Април', 'Мај', 'Јуни',
    'Јули', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'
  ];

  const dayNames = ['Пон', 'Вто', 'Сре', 'Чет', 'Пет', 'Саб', 'Нед'];

  const handleDateClick = (date) => {
    onDateSelect(format(date, 'yyyy-MM-dd'));
    onClose();
  };

  const goToPreviousMonth = () => {
    setViewDate(subMonths(viewDate, 1));
  };

  const goToNextMonth = () => {
    setViewDate(addMonths(viewDate, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setViewDate(today);
    onDateSelect(format(today, 'yyyy-MM-dd'));
    onClose();
  };

  const adjustedCalendarDays = [];
  let currentWeek = [];
  calendarDays.forEach((date, index) => {
    currentWeek.push(date);
    if ((index + 1) % 7 === 0 || index === calendarDays.length - 1) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      adjustedCalendarDays.push(currentWeek);
      currentWeek = [];
    }
  });

  return (
    <div className="res-edit-custom-datepicker-overlay" onClick={onClose}>
      <div className="res-edit-custom-datepicker" onClick={(e) => e.stopPropagation()}>
        <div className="res-edit-datepicker-header">
          <button className="res-edit-month-nav" onClick={goToPreviousMonth}>‹</button>
          <span className="res-edit-month-year">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          <button className="res-edit-month-nav" onClick={goToNextMonth}>›</button>
        </div>

        <div className="res-edit-datepicker-days-header">
          {dayNames.map((day) => (
            <div key={day} className="res-edit-day-header">{day}</div>
          ))}
        </div>

        <div className="res-edit-datepicker-days">
          {adjustedCalendarDays.map((week, weekIndex) => (
            <div key={weekIndex} className="res-edit-calendar-week">
              {week.map((date, dayIndex) => {
                if (!date) {
                  return <div key={`empty-${weekIndex}-${dayIndex}`} className="res-edit-day-cell res-edit-empty"></div>;
                }
                const isSelected = selectedDate && isSameDay(date, parseISO(selectedDate));
                const isToday = isSameDay(date, new Date());
                const isCurrentMonth = isSameMonth(date, viewDate);

                return (
                  <button
                    key={date.toString()}
                    className={`res-edit-day-cell ${isSelected ? 'res-edit-selected' : ''} ${isToday ? 'res-edit-today' : ''} ${!isCurrentMonth ? 'res-edit-other-month' : ''}`}
                    onClick={() => handleDateClick(date)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="res-edit-datepicker-footer">
          <button className="res-edit-today-btn" onClick={goToToday}>Денес</button>
          <button className="res-edit-close-btn" onClick={onClose}>Затвори</button>
        </div>
      </div>
    </div>
  );
};

const CustomTimePicker = ({ value, onChange, onClose }) => {
  const { data, isLoading } = useRestaurant();

  const reservationFrom = typeof data?.reservationFrom === 'number' ? Math.floor(data.reservationFrom) : 0;
  const reservationTo = typeof data?.reservationTo === 'number' ? Math.floor(data.reservationTo) : 23;

  const fromHour = Math.min(reservationFrom, reservationTo);
  const toHour = Math.max(reservationFrom, reservationTo);

  const initialHour = value ? parseInt(value.split(':')[0], 10) : fromHour;
  const initialMinute = value ? value.split(':')[1] : '00';
  const validInitialHour = Math.max(fromHour, Math.min(toHour, initialHour)).toString().padStart(2, '0');
  const validInitialMinute = ['00', '15', '30', '45'].includes(initialMinute) ? initialMinute : '00';

  const [tempHour, setTempHour] = useState(validInitialHour);
  const [tempMinute, setTempMinute] = useState(validInitialMinute);

  const hours = Array.from(
    { length: toHour - fromHour + 1 },
    (_, i) => (fromHour + i).toString().padStart(2, '0')
  );

  const minutes = ['00', '15', '30', '45'];

  const handleConfirm = () => {
    onChange(`${tempHour}:${tempMinute}`);
    onClose();
  };

  if (isLoading) {
    return <div>Loading restaurant data...</div>;
  }

  return (
    <div className="res-edit-custom-timepicker-overlay" onClick={onClose}>
      <div className="res-edit-custom-timepicker" onClick={(e) => e.stopPropagation()}>
        <div className="res-edit-timepicker-header">
          <h3>Избери време</h3>
        </div>

        <div className="res-edit-time-display">
          <span className="res-edit-time-value">{tempHour}:{tempMinute}</span>
        </div>

        <div className="res-edit-time-selectors">
          <div className="res-edit-time-column">
            <label>Час</label>
            <div className="res-edit-time-scroll">
              {hours.map((hour) => (
                <button
                  key={hour}
                  className={`res-edit-time-option ${tempHour === hour ? 'res-edit-selected' : ''}`}
                  onClick={() => setTempHour(hour)}
                >
                  {hour}
                </button>
              ))}
            </div>
          </div>

          <div className="res-edit-time-separator">:</div>

          <div className="res-edit-time-column">
            <label>Минути</label>
            <div className="res-edit-time-scroll">
              {minutes.map((minute) => (
                <button
                  key={minute}
                  className={`res-edit-time-option ${tempMinute === minute ? 'res-edit-selected' : ''}`}
                  onClick={() => setTempMinute(minute)}
                >
                  {minute}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="res-edit-timepicker-footer">
          <button className="res-edit-cancel-btn" onClick={onClose}>Откажи</button>
          <button className="res-edit-confirm-btn" onClick={handleConfirm}>Потврди</button>
        </div>
      </div>
    </div>
  );
};

const ReservationEditModal = ({ reservationId, tables, onClose, guestID, onSave }) => {
  const { data: reservation, isLoading, error } = useReservationById(reservationId);
  const { data: gostin, isLoading: isLoadingGuest } = useCustomerbyID(guestID);
  const { data: restaurantData, isLoading: isRestaurantLoading } = useRestaurant();
  const { mutate: updateReservation } = useUpdateReservationWhole();
  const { mutate: updateFreeTableInfo } = useUpdateReservationInfoFreeTable(); 
  const { profile } = useAuth(); 
  const { data: freeTableUser, isLoading: isLoadingUser } = useUser(
    reservation?.freeTableUser,
    { enabled: !!reservation?.freeTableUser }
  );

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    partySize: '',
    date: '',
    time: '',
    language: 'MK',
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission
  const modalContentRef = useRef(null);
  const [modalHeight, setModalHeight] = useState('auto');

  useEffect(() => {
    const updateModalHeight = () => {
      if (window.visualViewport) {
        setModalHeight(`${window.visualViewport.height}px`);
      } else {
        setModalHeight(`${window.innerHeight}px`);
      }
    };

    updateModalHeight();
    window.visualViewport?.addEventListener('resize', updateModalHeight);
    window.addEventListener('resize', updateModalHeight);

    return () => {
      window.visualViewport?.removeEventListener('resize', updateModalHeight);
      window.removeEventListener('resize', updateModalHeight);
    };
  }, []);

  const handleInputFocus = (e) => {
    setTimeout(() => {
      if (modalContentRef.current) {
        const input = e.target;
        const rect = input.getBoundingClientRect();
        const modalRect = modalContentRef.current.getBoundingClientRect();
        if (rect.bottom > (window.visualViewport?.height || window.innerHeight)) {
          modalContentRef.current.scrollTo({
            top: input.offsetTop - modalRect.top - 20,
            behavior: 'smooth',
          });
        }
      }
    }, 300);
  };

  useEffect(() => {
    if (reservation && gostin) {
      const reservationDateTime = parseISO(reservation.time);
      setFormData(prev => ({
        ...prev,
        name: reservation.name || '',
        phone: gostin.phone || '',
        email: gostin.email || '',
        partySize: reservation.partySize || '',
        date: format(reservationDateTime, 'yyyy-MM-dd'),
        time: format(reservationDateTime, 'HH:mm'),
        language: 'MK',
      }));
    }
  }, [reservation, gostin]);

  async function sendPushNotification(token, title, body) {
    try {
      const { data, error } = await supabaseFreeTable.functions.invoke('sendPushNotification', {
        body: { token, title, body },
      });
      if (error) {
        console.error('Error sending push notification:', error);
        return;
      }
      console.log('Push notification sent:', data);
    } catch (error) {
      console.error('Unexpected error:', error);
    }
  }

  if (isLoading || isLoadingGuest || isRestaurantLoading) {
    return (
      <div className="res-edit-modal-overlay" style={{ height: modalHeight }}>
        <div className="res-edit-modal">Се вчитува...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="res-edit-modal-overlay" style={{ height: modalHeight }}>
        <div className="res-edit-modal">Грешка: {error.message}</div>
      </div>
    );
  }

  if (!reservation) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prevErr => ({
        ...prevErr,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Името е задолжително';
    }
    if (!formData.partySize || formData.partySize < 1) {
      newErrors.partySize = 'Бројот на луѓе мора да биде најмалку 1';
    }
    if (!formData.date) {
      newErrors.date = 'Датата е задолжителна';
    }
    if (!formData.time) {
      newErrors.time = 'Времето е задолжително';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    setShowConfirm(true);
  };

  const confirmSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const parsedDate = parse(formData.date, 'yyyy-MM-dd', new Date());
      const [hours, minutes] = formData.time.split(':').map(Number);
      const reservationDateTime = set(parsedDate, { hours, minutes, seconds: 0, milliseconds: 0 });

      const updatedData = {
        ...reservation,
        name: formData.name,
        partySize: parseInt(formData.partySize),
        time: reservationDateTime.toISOString(),
      };

      if (freeTableUser && reservation.freeTableId) {
        updateFreeTableInfo({
          id: reservation.freeTableId,
          time: formData.time,
          date: formData.date,
          people: parseInt(formData.partySize)
        }); 
        await sendPushNotification(
          freeTableUser.expo_push_token,
          "Ажурирање на резервација",
          `Вашата резервација во ${restaurantData.name} беше ажурирана. Нови податоци: ${convertDateToShow(formData.date)} во ${formData.time} за ${parseInt(formData.partySize)} луѓе`
        );
      }

      await new Promise((resolve, reject) => {
        updateReservation(
          {
            reservationId,
            name: updatedData.name,
            time: updatedData.time,
            partySize: updatedData.partySize,
          },
          {
            onSuccess: () => resolve(),
            onError: (err) => reject(new Error(`Грешка при ажурирање на резервација: ${err.message}`)),
          }
        );
      });

      const { error: customerError } = await supabase
        .from('customers')
        .update({
          phone: formData.phone,
          email: formData.email,
        })
        .eq('id', guestID);

      if (customerError) {
        throw new Error(`Грешка при ажурирање на гостин: ${customerError.message}`);
      }

      if (formData.email) {
        const placeholders = {
          'Guest Name': formData.name || 'Guest',
          'Restaurant Name': restaurantData?.name || 'Our Restaurant',
          'New Reservation Date': convertDateToShow(formData.date),
          'New Reservation Time': convertTimeToShow(formData.time),
          'New Number of Guests': formData.partySize,
          'Restaurant Address': restaurantData?.location || 'Unknown Address',
          'Contact Info': restaurantData?.contactInfo || 'contact@freetable.mk',
        };

        const sendResult = await sendEmail({
          restaurantId: profile.restaurantid, 
          templateKey: 'edited',
          language: reservation.language,
          recipient: formData.email,
          placeholders,
        });

        if (!sendResult.success) {
          console.warn('Failed to send edited email:', sendResult.error);
          setErrors(prev => ({
            ...prev,
            email: `Резервацијата е ажурирана, но грешка при испраќање на ажурираниот имейл: ${sendResult.error}`,
          }));
        }
      }

      setShowConfirm(false);
      if (onSave) onSave();
      onClose();

    } catch (err) {
      console.error('Error during save:', err.message);
      setErrors(prev => ({
        ...prev,
        general: err.message,
      }));
      setShowConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const convertDateToShow = (date) => { 
    if (!date) {
      return "Внеси Датум";
    }
    const dateObj = new Date(date);
    let dateString = `${dateObj.getDate().toString().padStart(2,'0')}/${(dateObj.getMonth()+1).toString().padStart(2,'0')}/${dateObj.getFullYear()}`;
    return dateString; 
  };

  const convertTimeToShow = (time) => {
    if (!time) return "Избери време";
    const [hour, minute] = time.split(':');
    return `${hour}:${minute}`;
  };

  return (
    <div className="res-edit-modal-overlay" style={{ height: modalHeight }}>
      <div className="res-edit-modal" ref={modalContentRef}>
        <button className="res-edit-modal-close" onClick={onClose}>×</button>
        <h3>Измени резервација</h3>
        
        <form className="res-edit-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div className="res-edit-form-group">
            <label htmlFor="name">Име на резервацијата:</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              onFocus={handleInputFocus}
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="res-edit-error-text">{errors.name}</span>}
          </div>

          <div className="res-edit-form-group">
            <label htmlFor="phone">Телефонски број:</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onFocus={handleInputFocus}
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && <span className="res-edit-error-text">{errors.phone}</span>}
          </div>

          <div className="res-edit-form-group">
            <label htmlFor="email">Емаил адреса:</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onFocus={handleInputFocus}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="res-edit-error-text">{errors.email}</span>}
          </div>

          <div className="res-edit-form-row">
            <div className="res-edit-form-group">
              <label htmlFor="date">Дата:</label>
              <div 
                className="res-edit-custom-input-display"
                onClick={() => setShowDatePicker(true)}
              >
                {convertDateToShow(formData.date)}
              </div>
              {errors.date && <span className="res-edit-error-text">{errors.date}</span>}
            </div>

            <div className="res-edit-form-group">
              <label htmlFor="time">Време:</label>
              <div 
                className="res-edit-custom-input-display"
                onClick={() => setShowTimePicker(true)}
              >
                {convertTimeToShow(formData.time)}
              </div>
              {errors.time && <span className="res-edit-error-text">{errors.time}</span>}
            </div>
          </div>

          <div className="res-edit-form-group">
            <label htmlFor="partySize">Број на луѓе:</label>
            <input
              type="number"
              id="partySize"
              min="1"
              value={formData.partySize}
              onChange={(e) => handleInputChange('partySize', e.target.value)}
              onFocus={handleInputFocus}
              className={errors.partySize ? 'error' : ''}
            />
            {errors.partySize && <span className="res-edit-error-text">{errors.partySize}</span>}
          </div>

          {errors.contact && <span className="res-edit-error-text res-edit-general-error">{errors.contact}</span>}
          {errors.general && <span className="res-edit-error-text res-edit-general-error">{errors.general}</span>}

          <div className="res-edit-actions">
            <button 
              type="button" 
              className="res-edit-save-button" 
              onClick={handleSave} 
              disabled={isSubmitting}
            >
              Зачувај
            </button>
            <button 
              type="button" 
              className="res-edit-cancel-button" 
              onClick={onClose}
            >
              Откажи
            </button>
          </div>

          {showConfirm && (
            <div className="res-edit-confirm-prompt">
              <p>Дали сте сигурни дека сакате да ги зачувате промените?</p>
              <div className="res-edit-confirm-actions">
                <button onClick={confirmSave} disabled={isSubmitting}>Да</button>
                <button onClick={() => setShowConfirm(false)}>Не</button>
              </div>
            </div>
          )}
        </form>

        {showDatePicker && (
          <CustomDatePicker
            selectedDate={formData.date}
            onDateSelect={(date) => handleInputChange('date', date)}
            onClose={() => setShowDatePicker(false)}
          />
        )}

        {showTimePicker && (
          <CustomTimePicker
            value={formData.time}
            onChange={(time) => handleInputChange('time', time)}
            onClose={() => setShowTimePicker(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ReservationEditModal;